import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import {
  formatSheetRowPriceDisplay,
  formatSheetRowPriceInputValue,
  formatSheetRowPromotionDiscountInputValue,
  getSheetRowCategoryDisplay,
  getSheetRowDefaultRawName,
  parseSheetPriceInputToNumber,
  parseSheetPromotionDiscountInputToNumber,
  readSheetRowOfferDefaults,
  readSheetRowReasonCodesHint,
  readSheetRowSuggestedCatalogMaterialName,
  readSheetRowResolvedUserCategoryId,
  resolveSheetRematchDisplayedRawName,
  rowMeetsSheetConfirmMinimums,
  rowUnitRequiresKnownSelectValue,
  rowUsesCatalogDirectConfirm,
  type SheetCategoryOption,
  type SheetRematchByRowIndex,
  type SheetRematchPick,
  type SheetRowFieldPatch,
} from '../../../services/materialCommerceSheetService.ts';
import {
  coerceRawUnitToMaterialSelectValue,
  MATERIAL_UNIT_SELECT_OPTIONS,
} from '../../../utils/marketplace/materialUnitSelectOptions.ts';
import CustomSelect from '../../../pages/ProjectDocumentation/components/CustomSelect.tsx';
import styles from '../CommerceSheetReviewStep.module.scss';
import {
  descInputValue,
  descriptionOfferInputValue,
  isRowIncluded,
  priceInputValue,
  promotionDiscountInputValue,
  promotionOfferInputValue,
  sheetReviewRowFingerprint,
  unitInputValue,
  unitSelectTableValue,
} from './commerceSheetReviewStepHelpers.ts';

export interface CommerceSheetReviewTableRowProps {
  rowIndex: number;
  row: unknown;
  eff: unknown;
  patch: SheetRowFieldPatch | undefined;
  displayRow: number;
  catalogLinked: boolean;
  remPick: SheetRematchPick | undefined;
  remInit: SheetRematchPick;
  remCur: SheetRematchPick;
  categories: SheetCategoryOption[];
  categorySelectOptions: { value: string; label: string }[];
  excludedByRowIndex: Record<number, boolean>;
  rematchByRowIndex: SheetRematchByRowIndex;
  patchField: (rowIndex: number, patch: Partial<SheetRowFieldPatch>) => void;
  onRematchChange: (rowIndex: number, patch: Partial<SheetRematchPick>) => void;
  onExcludedChange?: (rowIndex: number, excluded: boolean) => void;
  onRemovePreviewRow?: (rowIndex: number) => void;
  rowMatchesStoredListing: (effRow: unknown) => boolean;
  rowHasPublishedCommerceMaterial: (effRow: unknown) => boolean;
  unchangedBootstrapRef: React.MutableRefObject<Set<number>>;
}

export const CommerceSheetReviewTableRow: React.FC<CommerceSheetReviewTableRowProps> = ({
  rowIndex: i,
  row,
  eff,
  patch,
  displayRow,
  catalogLinked,
  remPick,
  remInit,
  remCur,
  categories,
  categorySelectOptions,
  excludedByRowIndex,
  rematchByRowIndex,
  patchField,
  onRematchChange,
  onExcludedChange,
  onRemovePreviewRow,
  rowMatchesStoredListing,
  rowHasPublishedCommerceMaterial,
  unchangedBootstrapRef,
}) => {
  const rowExcluded = Boolean(excludedByRowIndex[i]);
  const resolvedCategoryId = readSheetRowResolvedUserCategoryId(eff);
  const userCatId = remPick?.userCategoryId;
  const fromPickNum = userCatId !== '' && userCatId != null ? Number(userCatId) : NaN;
  const effectiveCategoryId =
    Number.isFinite(fromPickNum) && fromPickNum > 0
      ? fromPickNum
      : resolvedCategoryId != null && resolvedCategoryId > 0
        ? resolvedCategoryId
        : null;
  const categorySelectOutlineInvalid = effectiveCategoryId == null;
  const rawEmpty = !catalogLinked && !resolveSheetRematchDisplayedRawName(eff, remPick).length;
  const catalogNameEmpty = catalogLinked && !getSheetRowDefaultRawName(eff).trim();
  const dataComplete = rowMeetsSheetConfirmMinimums(row, i, eff, rematchByRowIndex);
  const matchesStoredListingRow = rowMatchesStoredListing(eff);
  const hasPublishedCommerceMaterial = rowHasPublishedCommerceMaterial(eff);
  const wasSameOfferAtImport = unchangedBootstrapRef.current.has(i);
  const rowChangedFromImport =
    sheetReviewRowFingerprint(row, patch, remCur) !== sheetReviewRowFingerprint(row, undefined, remInit);
  const showCommerceMaterialBadge =
    hasPublishedCommerceMaterial &&
    !rowChangedFromImport &&
    (!wasSameOfferAtImport || matchesStoredListingRow);
  const rowIncomplete = !rowExcluded && !dataComplete;
  const inferredCat = getSheetRowCategoryDisplay(eff, categories);
  const reasonHint = readSheetRowReasonCodesHint(row);
  const offerDefaults = readSheetRowOfferDefaults(eff);
  const categorySelectValue = effectiveCategoryId != null ? String(effectiveCategoryId) : '';

  return (
    <tr className={rowExcluded ? styles.trExcluded : undefined}>
        <td
          className={`${styles.tdItem} ${rowIncomplete ? styles.tdItemIncomplete : styles.tdItemComplete}`}
        >
          <div className={styles.itemCell}>
            <span className={styles.itemOrdinal} aria-hidden>
              {displayRow}
            </span>
            <input
              type="checkbox"
              checked={isRowIncluded(excludedByRowIndex, i)}
              onChange={(e) => {
                const nextIncluded = e.target.checked;
                if (nextIncluded && !rowMeetsSheetConfirmMinimums(row, i, eff, rematchByRowIndex)) {
                  return;
                }
                onExcludedChange?.(i, !nextIncluded);
              }}
              aria-label={`Incluir ítem ${displayRow} al publicar. Desmarcá para no publicar.`}
              title="Marcado: se publica. Sin marcar: no se publica."
            />
          </div>
        </td>
        <td className={styles.td}>
          <div className={styles.cellStack}>
            {rowExcluded ? (
              <span className={styles.badgeSkip}>No publicar</span>
            ) : dataComplete ? (
              <span className={styles.badgeOk}>Listo</span>
            ) : (
              <span className={styles.badgeAction}>Completar datos</span>
            )}
            {showCommerceMaterialBadge ? (
              <span
                className={
                  matchesStoredListingRow ? styles.badgeCommerceListed : styles.badgeCommerceListedWarn
                }
              >
                {matchesStoredListingRow
                  ? 'Ya en tus materiales · misma oferta'
                  : 'Ya en tus materiales · otra oferta'}
              </span>
            ) : null}
            {onRemovePreviewRow ? (
              <button
                type="button"
                className={styles.estadoRemoveRow}
                onClick={() => onRemovePreviewRow(i)}
                aria-label="Eliminar este ítem de la lista de revisión"
              >
                Eliminar de la lista
              </button>
            ) : null}
            {reasonHint ? (
              <span className={styles.reasonHint} title={reasonHint}>
                {reasonHint}
              </span>
            ) : null}
          </div>
        </td>
        <td className={styles.tdGrow}>
          <div className={styles.cellStack}>
            {inferredCat !== '—' ? (
              <span className={styles.inferredCat}>Sugerida (IA): {inferredCat}</span>
            ) : null}
            <div className={styles.categorySelectWrap}>
              <CustomSelect
                value={categorySelectValue}
                onChange={(v) =>
                  onRematchChange(i, {
                    userCategoryId: v ? Number(v) : '',
                  })
                }
                options={categorySelectOptions}
                placeholder="Elegí categoría…"
                searchable
                searchPlaceholder="Buscar categoría…"
                outlineInvalid={categorySelectOutlineInvalid}
                selectVisualVariant="compact"
                title={`Categoría ítem ${displayRow}`}
              />
            </div>
          </div>
        </td>
        <td className={styles.tdGrow}>
          <div className={styles.nameOfferStack}>
            <label className={styles.offerField}>
              <span className={styles.offerLabel}>Nombre</span>
              {catalogLinked ? (
                <TextareaAutosize
                  className={`${styles.inputDesc} ${catalogNameEmpty ? styles.fieldNeedsValue : ''}`}
                  value={descInputValue(eff, patch)}
                  onChange={(e) => patchField(i, { rawName: e.target.value })}
                  minRows={1}
                  aria-label={`Nombre ítem ${displayRow}`}
                  aria-invalid={catalogNameEmpty}
                />
              ) : (
                <TextareaAutosize
                  className={`${styles.inputDesc} ${rawEmpty ? styles.fieldNeedsValue : ''}`}
                  value={rematchByRowIndex[i]?.rawName ?? ''}
                  onChange={(e) => onRematchChange(i, { rawName: e.target.value })}
                  placeholder="Como en tu listado"
                  minRows={1}
                  aria-invalid={rawEmpty}
                />
              )}
            </label>
            {catalogLinked && readSheetRowSuggestedCatalogMaterialName(row) ? (
              <div className={styles.catalogMatchCompare}>
                <div className={styles.catalogMatchPair}>
                  <span className={styles.catalogMatchLabel}>Nombre sugerido</span>
                  <span className={styles.catalogMatchValue}>
                    {readSheetRowSuggestedCatalogMaterialName(row)}
                  </span>
                </div>
                <label className={styles.catalogRejectMatch}>
                  <input
                    type="checkbox"
                    checked={
                      remPick?.rejectSuggestedCatalogMatch === true &&
                      remPick?.createCatalogMaterial === true
                    }
                    onChange={(e) => {
                      const on = e.target.checked;
                      onRematchChange(i, {
                        rejectSuggestedCatalogMatch: on,
                        createCatalogMaterial: on,
                      });
                    }}
                    aria-label="No es el material sugerido"
                  />
                  <span>No es el material sugerido</span>
                </label>
              </div>
            ) : null}
            <label className={styles.offerField}>
              <span className={styles.offerLabel}>Unidad</span>
              <div className={styles.unitSelectWrap}>
                <CustomSelect
                  value={unitSelectTableValue(eff, patch)}
                  onChange={(v) => patchField(i, { unit: v })}
                  options={MATERIAL_UNIT_SELECT_OPTIONS}
                  placeholder="Seleccioná una unidad"
                  searchable
                  searchPlaceholder="Buscar unidad…"
                  title={`Unidad ítem ${displayRow}`}
                  selectVisualVariant="compact"
                  outlineInvalidTone="warn"
                  outlineInvalid={
                    rowUnitRequiresKnownSelectValue(row, eff) ||
                    (rowUsesCatalogDirectConfirm(row) &&
                      remPick?.rejectSuggestedCatalogMatch === true &&
                      remPick?.createCatalogMaterial === true &&
                      coerceRawUnitToMaterialSelectValue(unitInputValue(eff, patch)) === '')
                  }
                />
              </div>
            </label>
            <label className={styles.offerField}>
              <span className={styles.offerLabel}>Descripción</span>
              <TextareaAutosize
                className={styles.inputDesc}
                value={descriptionOfferInputValue(eff, patch)}
                onChange={(e) => patchField(i, { description: e.target.value })}
                minRows={1}
                aria-label={`Descripción ítem ${displayRow}`}
              />
            </label>
            <label className={styles.offerField}>
              <span className={styles.offerLabel}>Bonificación</span>
              <input
                type="text"
                className={styles.inputDesc}
                value={promotionOfferInputValue(eff, patch)}
                onChange={(e) => patchField(i, { promotion: e.target.value })}
                aria-label={`Bonificación ítem ${displayRow}`}
              />
            </label>
            <label className={styles.offerField}>
              <span className={styles.offerLabel}>Descuento de promoción (%)</span>
              <input
                type="text"
                inputMode="decimal"
                className={styles.inputOfferNarrow}
                value={promotionDiscountInputValue(eff, patch)}
                onChange={(e) => patchField(i, { promotionDiscount: e.target.value })}
                onBlur={(e) => {
                  const raw = e.target.value.trim();
                  if (!raw) {
                    patchField(i, { promotionDiscount: '' });
                    return;
                  }
                  const n = parseSheetPromotionDiscountInputToNumber(raw);
                  if (n != null) {
                    patchField(i, { promotionDiscount: formatSheetRowPromotionDiscountInputValue(n) });
                  }
                }}
                aria-label={`Descuento de promoción en porcentaje, ítem ${displayRow}`}
              />
            </label>
          </div>
        </td>
        <td className={styles.td}>
          <div className={styles.cellStack}>
            {formatSheetRowPriceDisplay(row) !== '—' ? (
              <span className={styles.inferredCat}>Referencia: {formatSheetRowPriceDisplay(row)}</span>
            ) : null}
            <input
              type="text"
              inputMode="decimal"
              className={styles.inputPrice}
              value={priceInputValue(eff, patch)}
              onChange={(e) => patchField(i, { price: e.target.value })}
              onBlur={(e) => {
                const raw = e.target.value.trim();
                if (!raw) {
                  patchField(i, { price: '' });
                  return;
                }
                const n = parseSheetPriceInputToNumber(raw);
                if (n != null) {
                  patchField(i, { price: formatSheetRowPriceInputValue(n) });
                }
              }}
              aria-label={`Precio ítem ${displayRow}`}
              disabled={offerDefaults.unquoted}
            />
            <div className={styles.priceColumnChecks}>
              <label className={styles.priceColumnCheck}>
                <input
                  type="checkbox"
                  checked={offerDefaults.unquoted}
                  onChange={(e) => {
                    const on = e.target.checked;
                    if (on) {
                      patchField(i, { unquoted: true, valueAddedTax: false });
                    } else {
                      patchField(i, { unquoted: false });
                    }
                  }}
                />
                <span>No informar precio</span>
              </label>
              {!offerDefaults.unquoted ? (
                <label className={styles.priceColumnCheck}>
                  <input
                    type="checkbox"
                    checked={offerDefaults.valueAddedTax}
                    onChange={(e) => patchField(i, { valueAddedTax: e.target.checked })}
                  />
                  <span>IVA incluido en el precio</span>
                </label>
              ) : null}
            </div>
          </div>
        </td>
    </tr>
  );
};
